import {useEffect, useEffectEvent, useState} from 'react'

/**
 * Hook for detecting text overflow in reference token elements.
 *
 * Uses a single ResizeObserver on the container — when tokens are added, removed,
 * or the container resizes, the observer fires and we check for overflow.
 * Layout reads are deferred to rAF so the ResizeObserver callback stays off the
 * critical path.
 */
export function useOverflowDetection<T>(
  selectedReferences: T[],
  tokenElementsRef: React.RefObject<Array<HTMLElement | null> | null>,
  containerRef: React.RefObject<HTMLElement | null>,
  textElementSelector: string,
): boolean {
  const [hasOverflowingText, setHasOverflowingText] = useState(false)

  const checkForOverflow = useEffectEvent(() => {
    if (!tokenElementsRef.current) {
      setHasOverflowingText(false)
      return
    }

    // Phase 1: batch DOM queries (no layout forced)
    const textElements: Element[] = []
    for (const token of tokenElementsRef.current) {
      if (!token) continue
      const el = token.querySelector(textElementSelector)
      if (el) textElements.push(el)
    }

    // Phase 2: batch layout reads (single reflow, short-circuits on first hit)
    const hasOverflow = textElements.some(el => el.scrollWidth > el.clientWidth)
    setHasOverflowingText(hasOverflow)
  })

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let frameId: number | undefined
    const scheduleCheck = () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(checkForOverflow)
    }

    const resizeObserver = new ResizeObserver(scheduleCheck)
    resizeObserver.observe(container)

    // Also run once immediately for the initial state
    scheduleCheck()

    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
    }
  }, [containerRef])

  if (hasOverflowingText && selectedReferences.length === 0) {
    setHasOverflowingText(false)
  }

  return hasOverflowingText && selectedReferences.length > 0
}
